import { Link, NavLink } from "react-router-dom";
import { ShoppingBag, Menu, X, User, LogOut } from "lucide-react";
import { useState } from "react";
import { useCart } from "../lib/cart";
import { useAuth } from "../lib/auth";
import { trackEvent } from "../lib/analytics";
import SearchBox from "./SearchBox";
import SaleBanner from "./SaleBanner";

const LOGO = "/logo.png";

const navLinks = [
  { to: "/", label: "Home", end: true },
  { to: "/customize", label: "Customize" },
  { to: "/ready-made", label: "Ready-Made" },
  { to: "/dtf", label: "DTF Transfers" },
  { to: "/bulk-orders", label: "Bulk Orders" },
  { to: "/gallery", label: "Gallery" },
  { to: "/blog", label: "Blog" },
  { to: "/about", label: "About" },
  { to: "/faq", label: "FAQ" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const { items } = useCart();
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const count = items.reduce((s, it) => s + it.quantity, 0);

  return (
    <header data-testid="site-header" className="sticky top-0 z-50 bg-black/90 backdrop-blur-xl border-b border-[#222] w-full overflow-x-clip">
      <SaleBanner />
      {/* iter75b — layout fixes for laptop widths (1440-1600 px):
          • `min-w-0` on the main container lets flex children shrink
            properly instead of pushing siblings off-screen.
          • `max-w-full` overrides the fixed 1500px so we never exceed viewport.
          • `overflow-x-clip` on the outer <header> is a belt-and-braces
            safety net — even if a child overflows, the page won't scroll. */}
      <div className="max-w-[1500px] max-w-full mx-auto px-4 lg:px-8 h-20 flex items-center justify-between gap-3 lg:gap-5 min-w-0">
        <Link to="/" data-testid="header-logo" className="flex items-center gap-3 flex-shrink-0">
          <img src={LOGO} alt="Toronto Custom" className="h-20 w-20 sm:h-28 sm:w-28 object-contain" />
          <div className="hidden sm:block leading-none">
            <div className="font-display text-xl tracking-tight">TORONTO CUSTOM T-SHIRTS</div>
            <div className="font-mono text-[10px] tracking-[0.25em] text-zinc-500">PRINTED LOCALLY · TORONTO + GTA</div>
          </div>
        </Link>

        <nav className="hidden nav-fits:flex items-center gap-3 xl:gap-4 min-w-0">
          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/[^a-z]/g, "-")}`}
              className={({ isActive }) =>
                `font-ui text-[11px] font-bold uppercase tracking-wider transition-colors whitespace-nowrap ${
                  isActive ? "text-white" : "text-zinc-400 hover:text-white"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        {/* Desktop search — guaranteed minimum width, grows to fill */}
        <div className="hidden md:block flex-1 min-w-[180px] lg:min-w-[220px] mx-3">
          <SearchBox variant="desktop" />
        </div>

        <div className="flex items-center gap-3 flex-shrink-0">
          {/* Mobile search icon button (hidden when desktop search is visible) */}
          <div className="md:hidden">
            <SearchBox variant="mobile" />
          </div>

          <Link to="/quote" data-testid="header-quote-link" onClick={() => trackEvent("cta_quote_click", { cta: "header_quote", location: "header" })} className="hidden md:inline-flex font-ui text-[11px] font-bold uppercase tracking-wider px-4 py-2 border border-[var(--tc-red)] text-[var(--tc-red)] hover:bg-[var(--tc-red)] hover:text-white transition-colors">
            Quote
          </Link>

          {/* Account */}
          {user ? (
            <div className="relative">
              <button
                data-testid="header-account-btn"
                onClick={() => setAccountOpen((v) => !v)}
                className="flex items-center gap-2 px-3 py-2 border border-[#222] hover:border-[var(--tc-red)]"
              >
                <User size={16} />
                <span className="font-mono text-xs tracking-widest hidden sm:inline truncate max-w-[100px]">{user.name.split(" ")[0].toUpperCase()}</span>
              </button>
              {accountOpen && (
                <div className="absolute right-0 top-full mt-1 w-48 surface-card z-50" onMouseLeave={() => setAccountOpen(false)}>
                  <Link to="/account" data-testid="account-link" onClick={() => setAccountOpen(false)} className="block px-4 py-3 font-mono text-[11px] tracking-widest hover:bg-[#141414] border-b border-[#222]">
                    MY ORDERS
                  </Link>
                  <button data-testid="header-logout-btn" onClick={() => { logout(); setAccountOpen(false); }} className="w-full text-left px-4 py-3 font-mono text-[11px] tracking-widest hover:bg-[#141414] text-zinc-300 flex items-center gap-2">
                    <LogOut size={12} /> LOGOUT
                  </button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/login" data-testid="header-login-link" className="hidden md:inline-flex items-center gap-2 px-3 py-2 border border-[#222] hover:border-[var(--tc-red)]">
              <User size={16} />
              <span className="font-mono text-xs tracking-widest">SIGN IN</span>
            </Link>
          )}

          <Link to="/cart" data-testid="header-cart-link" className="relative flex items-center gap-2 px-3 py-2 border border-[#222] hover:border-[var(--tc-red)]">
            <ShoppingBag size={16} />
            <span className="font-mono text-xs tracking-widest hidden sm:inline">CART</span>
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-2 -right-2 bg-[var(--tc-red)] text-white text-[10px] font-bold w-5 h-5 flex items-center justify-center font-mono">
                {count}
              </span>
            )}
          </Link>
          <button data-testid="mobile-menu-btn" onClick={() => setOpen((v) => !v)} className="nav-fits:hidden p-2 border border-[#222]">
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="nav-fits:hidden border-t border-[#222] bg-black">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((l) => (
              <NavLink key={l.to} to={l.to} end={l.end} onClick={() => setOpen(false)} className="font-ui text-sm font-bold uppercase tracking-wider text-zinc-300 py-3 border-b border-[#222]">
                {l.label}
              </NavLink>
            ))}
            {user ? (
              <Link to="/account" onClick={() => setOpen(false)} className="font-ui text-sm font-bold uppercase tracking-wider text-[var(--tc-red)] py-3">
                My Account →
              </Link>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} className="font-ui text-sm font-bold uppercase tracking-wider text-[var(--tc-red)] py-3">
                Sign In · Track Order →
              </Link>
            )}
            <Link to="/quote" onClick={() => setOpen(false)} className="font-ui text-sm font-bold uppercase tracking-wider text-[var(--tc-red)] py-3">
              Request a Quote →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
